import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userEmail"); // optional if you stored it
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const navigationItems = [
    {
      label: 'Platform Overview',
      path: '/platform-overview-dashboard',
      icon: 'BarChart3',
      tooltip: 'Comprehensive platform KPIs and system health monitoring'
    },
    {
      label: 'Revenue Intelligence',
      path: '/revenue-intelligence-dashboard',
      icon: 'TrendingUp',
      tooltip: 'Financial analytics and billing performance tracking'
    },
    {
      label: 'Tenant Analytics',
      path: '/tenant-management-analytics',
      icon: 'Users',
      tooltip: 'Comprehensive tenant health monitoring and subscription lifecycle'
    },
    {
      label: 'Usage Monitoring',
      path: '/usage-analytics-performance-monitoring',
      icon: 'Activity',
      tooltip: 'Platform utilization analytics and performance tracking'
    },
    {
      label: 'Support Operations',
      path: '/support-operations-intelligence',
      icon: 'HeadphonesIcon',
      tooltip: 'Customer support analytics and administrative activity monitoring'
    }
  ];

  const isActiveRoute = (path) => location.pathname === path;

  const Logo = () => (
    <Link 
      to="/platform-overview-dashboard" 
      className={`flex items-center transition-all duration-300 ${
        isCollapsed ? 'justify-center' : 'space-x-3'
      }`}
    >
      <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-elevation-2">
        <Icon name="Shield" size={24} color="white" />
      </div>
      {!isCollapsed && (
        <div className="flex flex-col">
          <span className="text-xl font-bold text-text-primary tracking-tight">
            SuperAdmin
          </span>
          <span className="text-sm text-text-secondary font-medium">
            Dashboard
          </span>
        </div>
      )}
    </Link>
  );

  if (isMobile) {
    return (
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-100 bg-card border-t border-border shadow-elevation-3">
        <nav className="flex items-center justify-around py-2">
          {navigationItems.slice(0, 4).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-all duration-150
                ${isActiveRoute(item.path)
                  ? 'text-primary bg-primary/10' :'text-text-secondary hover:text-text-primary'
                }
              `}
              title={item.tooltip}
            >
              <Icon name={item.icon} size={20} />
              <span className="text-xs font-medium truncate max-w-16">
                {item.label.split(' ')[0]}
              </span>
            </Link>
          ))}
          
          {/* More button for additional items */}
          <div className="relative group">
            <button className="flex flex-col items-center space-y-1 px-3 py-2 rounded-lg text-text-secondary hover:text-text-primary transition-all duration-150">
              <Icon name="MoreHorizontal" size={20} />
              <span className="text-xs font-medium">More</span>
            </button>
            
            {/* Mobile More Menu */}
            <div className="absolute bottom-full right-0 mb-2 w-48 bg-popover border border-border rounded-lg shadow-elevation-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="py-2">
                {navigationItems.slice(4).map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center space-x-3 px-4 py-2 text-sm transition-colors duration-150
                      ${isActiveRoute(item.path)
                        ? 'bg-primary/10 text-primary' :'text-text-secondary hover:text-text-primary hover:bg-muted/50'
                      }
                    `}
                  >
                    <Icon name={item.icon} size={16} />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>
      </div>
    );
  }

  return (
    <aside className={`
      fixed left-0 top-0 z-100 h-full bg-card border-r border-border shadow-elevation-2 transition-all duration-300 ease-dashboard
      ${isCollapsed ? 'w-20' : 'w-60'}
    `}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b border-border ${
          isCollapsed ? 'px-4' : ''
        }`}>
          <Logo />
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(true)}
              className="text-text-secondary hover:text-text-primary"
            >
              <Icon name="PanelLeftClose" size={18} />
            </Button>
          )}
        </div>

        {/* Collapsed Toggle */}
        {isCollapsed && (
          <div className="flex justify-center p-4 border-b border-border">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(false)}
              className="text-text-secondary hover:text-text-primary"
            >
              <Icon name="PanelLeftOpen" size={18} />
            </Button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                group flex items-center rounded-lg transition-all duration-150 ease-dashboard relative
                ${isCollapsed ? 'justify-center p-3' : 'space-x-3 px-4 py-3'}
                ${isActiveRoute(item.path)
                  ? 'bg-primary/10 text-primary border-r-4 border-primary shadow-elevation-1'
                  : 'text-text-secondary hover:text-text-primary hover:bg-muted/50'
                }
              `}
              title={isCollapsed ? item.tooltip : ''}
            >
              <Icon name={item.icon} size={20} />
              {!isCollapsed && (
                <span className="font-medium">{item.label}</span>
              )}
              
              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-elevation-3 text-sm font-medium whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-1000">
                  {item.label}
                  <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-popover border-l border-b border-border rotate-45"></div>
                </div>
              )}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className={`p-4 border-t border-border ${isCollapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center transition-all duration-300 ${
            isCollapsed ? 'justify-center' : 'space-x-3'
          }`}>
            <div className="w-8 h-8 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center">
              <Icon name="User" size={16} color="white" />
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">
                  Admin User
                </p>
                <p className="text-xs text-text-secondary truncate">
                  admin@superadmin.com
                </p>
              </div>
            )}
          </div>
          
          {!isCollapsed && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
              <Button variant="ghost" size="sm" className="text-text-secondary hover:text-text-primary">
                <Icon name="Settings" size={16} />
              </Button>
              <Button variant="ghost" size="sm" className="text-text-secondary hover:text-text-primary">
                <Icon name="HelpCircle" size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-error hover:bg-error/10"
                onClick={handleLogout}
              >
                <Icon name="LogOut" size={16} />
              </Button>

            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;